import React, { useEffect, useState } from 'react';
import { Modal, Form, Select, Input, Button, Typography, message } from 'antd';
import { useTranslation } from 'react-i18next';
import updateUserProfile from '../utils/user/updateUserProfile';
import { getCurrentUser } from '../utils/userStorage';
import { toast } from 'react-hot-toast';

const { Paragraph, Text } = Typography;

export default function ProfileModal({ open, onClose, isEditing = false }) {
  const { t } = useTranslation();
  const specialties = t('survey.specialties', { returnObjects: true });
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      const user = getCurrentUser();
      if (user?.profile) {
        form.setFieldsValue(user.profile);
      }
    }
  }, [open, form]);

  const handleFinish = async (values) => {
    setIsSubmitting(true);
    try {
      await updateUserProfile(values);
      toast.success(t('common.saved'));
      onClose(); // Close modal after successful save
    } catch (error) {
      toast.error(t('common.errors.general'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const visibleFields = [
    { name: 'full_name', label: t('profile_modal.fields.full_name'), required: true },
    { name: 'specialty', label: t('profile_modal.fields.specialty'), required: true },
    { name: 'license_type', label: t('profile_modal.fields.license_type') },
    { name: 'license_number', label: t('profile_modal.fields.license_number') },
    { name: 'email', label: t('profile_modal.fields.email') },
    { name: 'clinic_name', label: t('profile_modal.fields.clinic_name') },
    { name: 'phone', label: t('profile_modal.fields.phone') },
    { name: 'address', label: t('profile_modal.fields.address') },
  ];

  const shownFields = isEditing
    ? visibleFields
    : visibleFields.filter((field) => ['full_name', 'specialty', 'license_number'].includes(field.name));

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={ isEditing ?  t('profile_modal.edit_title') : t('profile_modal.title')}
      centered
    >
      <Paragraph>
        {t('profile_modal.subtitle')}
      </Paragraph>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
      >
        {shownFields.map((field) => (
          <Form.Item
            key={field.name}
            label={<Text type="secondary">{field.label}</Text>}
            name={field.name}
            rules={
              field.required
                ? [{ required: true, message: `${field.label} ${t('common.required')}` }]
                : []
            }
          >
            {field.name === 'specialty' ? (
              <Select
                placeholder={t('profile_modal.fields.specialty')}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {Object.entries(specialties).map(([key, value]) => (
                  <Select.Option key={key} value={key} style={{ fontSize: '16px' }}>
                    {value}
                  </Select.Option>
                ))}
              </Select>
            ) : (
              <Input type={field.name === 'email' ? 'email' : 'text'} style={{ fontSize: '16px' }} />
            )}
          </Form.Item>
        ))}

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            loading={isSubmitting}
          >
            {t('common.save')}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
}